import React from "react"
import { graphql, Link } from "gatsby"
import Img from "gatsby-image"
import Layout from "../components/Layout"
import PageHero from "../components/PageHero"
import "../styles/news.scss"
import "../styles/caseStudy.scss"

/* ── Icons ────────────────────────────────────────────────────────── */
const IconDoc = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
)

const IconDownload = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const IconTrend = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
)

const IconCloud = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
  </svg>
)

const IconMobile = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
)

/* ── Data ─────────────────────────────────────────────────────────── */
const ANNOUNCEMENTS = [
  {
    key: "ai-eng",
    date: "Q1 2026",
    tag: "Engineering",
    tagBg: "#1b2d8e",
    accentColor: "#60a5fa",
    title: "AI-Augmented Engineering: 30%–80% Faster Delivery, Built Into Every Engagement",
    desc: "AI coding partners are now woven through our full delivery loop — from spec to review to deployment. The result is a 50% reduction in shipping time that clients see as a headline benefit on every project we take on.",
  },
  {
    key: "ai-audit",
    date: "Q1 2026",
    tag: "Practice",
    tagBg: "#6366f1",
    accentColor: "#a5b4fc",
    title: "The AI Audit: Map Your Bottlenecks and Get a 90-Day Roadmap in 14 Days",
    desc: "A focused engagement where we map your live process bottlenecks and deliver a clear 90-day AI roadmap. Several working sessions with your team are all it takes — clients have seen a 30% improvement in project delivery speed as a direct result.",
  },
  {
    key: "outcome-pods",
    date: "Q1 2026",
    tag: "Operations",
    tagBg: "#0d9488",
    accentColor: "#5eead4",
    title: "Outcome Pods: Cross-Functional Teams That Own a Customer Result End to End",
    desc: "Visiomate has restructured around Outcome Pods — small, cross-functional teams assigned to a single customer outcome rather than a function. Each pod owns discovery, build, and handover, eliminating the handoff gaps that slow most software engagements.",
  },
]

const INDUSTRY_UPDATES = [
  {
    Icon: IconTrend,
    date: "Q1 2026",
    title: "The SaaSpocalypse: $2 Trillion Wiped as Autonomous Agents Break the Per-Seat Model",
    desc: "The launches of Claude Cowork and OpenAI's Project Operator triggered the worst software selloff since the dot-com bust. Roughly $2 trillion in software market cap was erased in early 2026 as the per-seat licensing model that built the SaaS industry came under existential pressure from fully autonomous agents.",
  },
  {
    Icon: IconCloud,
    date: "Q1 2026",
    title: "AI Agents Now Writing Production Code at Scale — Engineering Capacity No Longer the Bottleneck",
    desc: "Delivery Hero's Herogen agent produces coding output equivalent to 130 senior engineers. Claude Code now authors roughly 4% of all GitHub commits globally, with projections exceeding 20% by year-end. For the first time, human engineering headcount is no longer the primary constraint on software delivery speed.",
  },
  {
    Icon: IconMobile,
    date: "Q1 2026",
    title: "Outcome-Based Pricing Displaces Per-Seat SaaS as Enterprises Demand Usage Contracts",
    desc: "ServiceNow launched 'Agentic ACV,' billing for tasks completed rather than seats filled. Salesforce, Adobe, and HubSpot followed. Pure per-seat pricing fell from 21% to 15% of SaaS vendors, with 70% of enterprises now requiring usage- or outcome-based contracts before signing.",
  },
]

const MILESTONES = [
  {
    imgKey: "newsCard4",
    date: "Ongoing — Weekly",
    tag: "HR Initiative",
    tagBg: "#6366f1",
    title: "Learning Circles — Employee Knowledge Enhancement",
    desc: "A weekly event managed by Visiomate's HR team to foster continuous learning, knowledge sharing, and professional growth across the company.",
  },
  {
    imgKey: "newsCard5",
    date: "Coming Q2 2024",
    tag: "Upcoming",
    tagBg: "#0ea5e9",
    title: "Bootcamp for Aspiring Mobile Developers",
    desc: "A comprehensive bootcamp covering cross-platform mobile development, real-world project experience, and mentorship from senior engineers.",
  },
  {
    imgKey: "newsCard6",
    date: "Jan 10, 2023",
    tag: "Growth",
    tagBg: "#16a34a",
    title: "Moved to a Better and Bigger Office Environment",
    desc: "As part of our commitment to an excellent work environment, Visiomate relocated to a larger, modern workspace to support our growing team.",
  },
]

const DOWNLOADS = [
  {
    type: "White Paper",
    typeColor: "#60a5fa",
    title: "Technical White Papers",
    desc: "In-depth documents covering ERP integration patterns, Odoo architecture decisions, and AI implementation frameworks for enterprise deployments.",
    link: "/WhitepapereBooks",
  },
  {
    type: "eBook",
    typeColor: "#4ade80",
    title: "Product eBooks",
    desc: "Comprehensive guides to Visioedge, YAZO, and Viztos — covering configuration, best practices, and real-world use cases for each product.",
    link: "/WhitepapereBooks",
  },
  {
    type: "Guide",
    typeColor: "#fbbf24",
    title: "Implementation Guides",
    desc: "Step-by-step playbooks for Odoo and Dynamics 365 projects — from scoping and data migration to go-live readiness and post-launch support.",
    link: "/WhitepapereBooks",
  },
]

/* ── Component ────────────────────────────────────────────────────── */
const News = ({ data }) => (
  <div className="CaseStudeies news">
    <Layout
      pageTitle="Visiomate — News &amp; Resources"
      descrip="Company announcements, industry updates, team milestones, and downloadable resources from Visiomate."
    >
      <PageHero
        eyebrow="Latest from Visiomate"
        title="News & Updates"
        description="Company announcements, industry insights, team milestones, and the stories behind our work — all in one place."
      />

      {/* ── 1. Company Announcements ── */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <span className="vm-news-eyebrow vm-news-eyebrow--dark">Company Announcements</span>
            <h2 className="vm-news-heading vm-news-heading--dark">Latest from Visiomate</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Key client wins and product launches from our team.
            </p>
          </div>
          <div className="vm-news-grid">
            {ANNOUNCEMENTS.map(item => (
              <div key={item.key} className="vm-news-card vm-news-card--light">
                <div className="vm-ann-card__header" style={{ background: "#eff6ff" }}>
                  <span
                    className="vm-news-card__badge"
                    style={{ background: "#dbeafe", color: "#1b2d8e", position: "static" }}
                  >
                    {item.tag}
                  </span>
                  <span className="vm-ann-card__date-pill" style={{ color: "#3b82f6" }}>{item.date}</span>
                </div>
                <div className="vm-news-card__body">
                  <h3 className="vm-news-card__title vm-news-card__title--dark">{item.title}</h3>
                  <p className="vm-news-card__desc vm-news-card__desc--dark">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 2. Industry Updates ── */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <span className="vm-news-eyebrow vm-news-eyebrow--dark">
              Industry Updates
            </span>
            <h2 className="vm-news-heading vm-news-heading--dark">
              Trends Shaping Our Work
            </h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Insights on the industries and technologies we work with every
              day.
            </p>
          </div>
          <div className="vm-news-updates">
            {INDUSTRY_UPDATES.map(({ Icon, date, title, desc }) => (
              <div key={title} className="vm-news-update">
                <div className="vm-news-update__icon">
                  <Icon />
                </div>
                <span className="vm-news-update__date">{date}</span>
                <h3 className="vm-news-update__title">{title}</h3>
                <p className="vm-news-update__desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Team Milestones ── */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <span className="vm-news-eyebrow vm-news-eyebrow--dark">
              Team Milestones
            </span>
            <h2 className="vm-news-heading vm-news-heading--dark">
              Life Inside Visiomate
            </h2>
            <p className="vm-news-sub vm-news-sub--dark">
              How we grow, learn, and work together as a team.
            </p>
          </div>
          <div className="vm-news-grid">
            {MILESTONES.map(item => (
              <div key={item.imgKey} className="vm-news-card vm-news-card--light">
                <div className="vm-news-card__img">
                  {data[item.imgKey] && (
                    <Img
                      fluid={data[item.imgKey].childImageSharp.fluid}
                      alt={item.title}
                      style={{ height: "100%" }}
                      imgStyle={{ objectFit: "cover" }}
                    />
                  )}
                  <span
                    className="vm-news-card__badge"
                    style={{ background: item.tagBg }}
                  >
                    {item.tag}
                  </span>
                </div>
                <div className="vm-news-card__body">
                  <span className="vm-news-card__date vm-news-card__date--dark">
                    {item.date}
                  </span>
                  <h3 className="vm-news-card__title vm-news-card__title--dark">
                    {item.title}
                  </h3>
                  <p className="vm-news-card__desc vm-news-card__desc--dark">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. Downloads ── */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <span className="vm-news-eyebrow vm-news-eyebrow--dark">Downloads</span>
            <h2 className="vm-news-heading vm-news-heading--dark">Knowledge Resources</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Technical documents and implementation guides to help you succeed.
            </p>
          </div>
          <div className="vm-news-dl-grid">
            {DOWNLOADS.map(item => (
              <div key={item.title} className="vm-news-dl-card vm-news-dl-card--light">
                <div className="vm-news-dl-card__icon">
                  <IconDoc />
                </div>
                <span
                  className="vm-news-dl-card__type"
                  style={{ color: item.typeColor }}
                >
                  {item.type}
                </span>
                <h3 className="vm-news-dl-card__title">{item.title}</h3>
                <p className="vm-news-dl-card__desc">{item.desc}</p>
                <Link to={item.link} className="vm-news-dl-card__btn">
                  <IconDownload /> Download Now
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  </div>
)

export default News

export const query = graphql`
  query NewsImages {
    newsCard1: file(relativePath: { eq: "news/Bigjerryfencing.jpg" }) {
      childImageSharp {
        fluid { ...GatsbyImageSharpFluid }
      }
    }
    newsCard2: file(relativePath: { eq: "news/yazoPOS1.png" }) {
      childImageSharp {
        fluid { ...GatsbyImageSharpFluid }
      }
    }
    newsCard3: file(relativePath: { eq: "news/visioedge_card.png" }) {
      childImageSharp {
        fluid { ...GatsbyImageSharpFluid }
      }
    }
    newsCard4: file(relativePath: { eq: "news/Circle.jpg" }) {
      childImageSharp {
        fluid { ...GatsbyImageSharpFluid }
      }
    }
    newsCard5: file(relativePath: { eq: "news/bootcamp.jpg" }) {
      childImageSharp {
        fluid { ...GatsbyImageSharpFluid }
      }
    }
    newsCard6: file(relativePath: { eq: "news/good_environement4.jpg" }) {
      childImageSharp {
        fluid { ...GatsbyImageSharpFluid }
      }
    }
  }
`
